<script>
	import model from "../../stores/modelStore";
	import lookups from "../../stores/lookupStore";
	import { registerPageComplete } from "../../tealium";
	import { projectSettings, acceptedFileUploadList } from "../../config";
	import { isValid, validator } from "../../validation";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import Checkbox from "../../components/Checkbox.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import MultiSelectAutoComplete from "../../components/MultiSelectAutoComplete.svelte";
	import Select from "../../components/Select.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import ClassActionEmailAddress from "../../components/ClassActionEmailAddress.svelte";
	import ClassActionPhoneNumber from "../../components/ClassActionPhoneNumber.svelte";

	// props
	export let persistUrl = null;
	export let pageName = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	let buttons = null;
	let showEmptyUploadedFilesError = false;
	
	let options = {
		url: "/api/file-upload?token=" + $model.registrationToken,
		acceptedFiles: acceptedFileUploadList,
		fileUploadToken: $model.registrationToken
	};

	let otherSuperProduct = $lookups.superProduct.filter((m) => m.label.startsWith("Other"))[0];
	let unsureInsurance = $lookups.insurance.filter((m) => m.label.startsWith("Unsure"))[0];
	let unsureFund = $lookups.fund.filter((m) => m.label.startsWith("Unsure"))[0];
	const unsureInsuranceId = unsureInsurance ? unsureInsurance.value : null;
	const unsureFundId = unsureFund ? unsureFund.value : null;

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	$model.claimDetails.superProducts = $model.claimDetails.superProducts || [];

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		// let hasValidDocumentList = showDropZone ? (Array.isArray($model.documentList) && $model.documentList.length > 0) : true;
		// if (!hasValidDocumentList) {
		// 	showEmptyUploadedFilesError = true;
		// 	errors.push(document.getElementById("dropzone"));
		// } else {
		// 	showEmptyUploadedFilesError = false;
		// }

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(response);
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	async function handleWhenBecomeMemberUknown(e) {
		if (e && e.target.checked) {
			$model.claimDetails.whenBecomeFundMemberYearId = null;
		}
	}

	async function handleTransferredOutYearUknown(e) {
		if (e && e.target.checked) {
			$model.claimDetails.transferredOutYearId = null;
		}
	}

	async function handleChangedInsurances(e) {
		const valueId = parseInt(e.target.value);
		if (e.target.checked) {
			if (valueId === unsureInsuranceId) {
				// only allow unsure insurance to be selected
				$model.claimDetails.insurances = [];
			} else {
				// remove unsure insurance if another insurance is selected
				const index = $model.claimDetails.insurances.indexOf(unsureInsuranceId);
				if (index !== -1) {
					$model.claimDetails.insurances.splice(index, 1);
				}
			}

			$model.claimDetails.insurances.push(valueId);
			$model.claimDetails.insurances = [...$model.claimDetails.insurances];
		}
	}

	async function handleChangedFunds(e) {
		const valueId = parseInt(e.target.value);
		if (e.target.checked) {
			if (valueId === unsureFundId) {
				// only allow unsure fund to be selected
				$model.claimDetails.funds = [];
			} else {
				// remove unsure fund if another fund is selected
				const index = $model.claimDetails.funds.indexOf(unsureFundId);
				if (index !== -1) {
					$model.claimDetails.funds.splice(index, 1);
				}
			}

			$model.claimDetails.funds.push(valueId);
			$model.claimDetails.funds = [...$model.claimDetails.funds];
		}
	}

	$: showOtherSuperProduct = $model.claimDetails.superProducts.filter((m) => m.value == otherSuperProduct.value).length > 0;
	$: showDropZone = $model.claimDetails.hasDocumentation === "Yes";

	$model.documentList = $model.documentList || [];

</script>

<PageSection>
	<Form {disabled} id="form-claim-details" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Claim Details</h1>
				<p>
					If you are unable to complete any part of this section of the form, we are here to assist you. 
					Please contact us on <ClassActionPhoneNumber/> or via email at <ClassActionEmailAddress/>.
				</p>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn cols="12">
					<CheckboxButtonList
						{disabled}
						name="super-funds"
						id="super-funds"
						label="What is the name of your superannuation fund?"
						bind:selected={$model.claimDetails.funds}
						optionList={$lookups.fund}
						on:change={handleChangedFunds}
						validator="checkboxRequired"
						validatorValue="1"
						validatorMessage="At least one selection is required"
						valueProperty="value"
						labelProperty="label" />
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn>
					<YesNoGroup
						required
						{disabled}
						name="know-super-product-name"
						label="Do you know the name of your superannuation product?"
						bind:value={$model.claimDetails.knowProductName} />
				</FormColumn>
			</FormRow>

			{#if $model.claimDetails.knowProductName === "Yes"}
				<FormRow>
					<FormColumn>
						<MultiSelectAutoComplete
							label="What is the name of your superannuation product?"
							placeholder="Search for superannuation product..."
							optionList={$lookups.superProduct}
							bind:selectedItems={$model.claimDetails.superProducts}
							otherOption={otherSuperProduct}
							labelProperty="label"
							valueProperty="value"
							required={true}
							{disabled} />
					</FormColumn>
				</FormRow>
				{#if showOtherSuperProduct}
					<FormRow>
						<FormColumn>
							<TextArea
								required
								{disabled}
								label="Please provide the name(s) of your Other superannuation product(s)."
								bind:value={$model.claimDetails.superProductOther} />
						</FormColumn>
					</FormRow>
				{/if}
			{/if}

			<FormRow>
				<FormColumn cols="7">
					<Select
						label="What year did you become a member of the fund?"
						required={!$model.claimDetails.whenBecomeFundMemberUnknow}
						disabled={disabled || $model.claimDetails.whenBecomeFundMemberYearUnknown}
						id="year-become-member"
						bind:value={$model.claimDetails.whenBecomeFundMemberYearId}
						optionList={$lookups.becomeFundMemberYear}
						textProperty="label"
						valueProperty="value"></Select>
				</FormColumn>
				<FormColumn cols="5" class="d-flex align-items-end">
					<div class="checkbox-container">
						<Checkbox
							{disabled}
							name="when-become-fund-member-unknown"
							id="when-become-fund-member-unknown"
							label="Unknown"
							bind:checked={$model.claimDetails.whenBecomeFundMemberYearUnknown}
							on:change={handleWhenBecomeMemberUknown}
						></Checkbox>
					</div>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn>
					<RadioGroup
						required
						{disabled}
						name="how-become-member"
						label="How did you become a member of the fund?"
						optionList={$lookups.howBecomeMember}
						bind:value={$model.claimDetails.membershipMethod}
						isVertical={true}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn>
					<RadioGroup
						{disabled}
						required
						name="have-insurance"
						label="Do you have insurance within the fund?"
						optionList={[
							{ label: "Yes", value: "Yes" },
							{ label: "No", value: "No" },
							{ label: "Unsure", value: "Unsure" },
						]}
						bind:value={$model.claimDetails.haveFundInsurance} />
				</FormColumn>
			</FormRow>
			{#if $model.claimDetails.haveFundInsurance === "Yes"}
				<FormRow>
					<FormColumn>
						<CheckboxButtonList
							{disabled}
							name="insurances"
							id="insurances"
							label="What type of insurance do you hold under the fund? Please select all relevant answers."
							bind:selected={$model.claimDetails.insurances}
							optionList={$lookups.insurance}
							on:change={handleChangedInsurances}
							validator="checkboxRequired"
							validatorValue="1"
							validatorMessage="At least one selection is required"
							valueProperty="value"
							labelProperty="label" />
					</FormColumn>
				</FormRow>
				<FormRow>
					<FormColumn>
						<RadioGroup
							{disabled}
							required
							name="still-insured"
							label="Do you still hold insurance through the fund?"
							optionList={[
								{ label: "Yes", value: "Yes" },
								{ label: "No", value: "No" },
								{ label: "Unsure", value: "Unsure" },
							]}
							bind:value={$model.claimDetails.stillInsuredThroughFund} />
					</FormColumn>
				</FormRow>
			{/if}

			<FormRow>
				<FormColumn>
					<YesNoGroup
						required
						{disabled}
						name="transferred-out"
						label="Have you transferred out of your superannuation product?"
						bind:value={$model.claimDetails.transferredOut} />
				</FormColumn>
			</FormRow>
			{#if $model.claimDetails.transferredOut === "Yes"}
				<FormRow>
					<FormColumn cols="7">
						<Select
							label="What year did you transfer out?"
							required={!$model.claimDetails.transferredOutYearUnknown}
							disabled={disabled || $model.claimDetails.transferredOutYearUnknown}
							id="year-transferred-out"
							bind:value={$model.claimDetails.transferredOutYearId}
							optionList={$lookups.transferredOutYear}
							textProperty="label"
							valueProperty="value"></Select>
					</FormColumn>
					<FormColumn cols="5" class="d-flex align-items-end">
						<div class="checkbox-container">
							<Checkbox
								{disabled}
								name="transferred-out-year-unknown"
								id="transferred-out-year-unknown"
								label="Unknown"
								bind:checked={$model.claimDetails.transferredOutYearUnknown}
								on:change={handleTransferredOutYearUknown}
							></Checkbox>
						</div>
					</FormColumn>
				</FormRow>
			{/if}

			<FormRow>
				<FormColumn>
					<YesNoGroup
						required
						{disabled}
						name="hasDocumentation"
						label="Do you have any supporting documentation?"
						bind:value={$model.claimDetails.hasDocumentation} />
				</FormColumn>
			</FormRow>
			<div hidden={showDropZone ? null : true}>
				<FormRow>
					<FormColumn>
						<label for="fileUpload">Please upload a copy of your supporting documents.</label>
						<DropZone
							{disabled}
							{options}
							id="dropzone"
							message="Upload files for your registration."
							bind:documentList={$model.documentList}>
						</DropZone>
					</FormColumn>
				</FormRow>
				<div class={showEmptyUploadedFilesError ? "error-message" : "invisible"}>
					At least one uploaded file is required
				</div>
			</div>
			<FormRow>
				<FormColumn>
					<TextArea
						{disabled}
						label="Is there anything else you want to tell us?"
						bind:value={$model.claimDetails.additionalInformation} />
				</FormColumn>
			</FormRow>
		</div>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>

						